<template>
  <div class="app-container shiftworkhours-page">
    <!-- 查询条件 -->
    <div class="filter-container">
      <el-form :model="queryParams" inline label-width="80px">
        <el-form-item label="车间">
          <el-select
            v-model="queryParams.DpIds"
            multiple
            collapse-tags
            clearable
            placeholder="请选择车间"
            style="width: 200px"
          >
            <el-option
              v-for="item in dpOptions"
              :key="item.DpId"
              :label="item.DpName"
              :value="item.DpId"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="设备编号">
          <page-select
            v-model="queryParams.EquipmentIds"
            :load-api="loadEquipmentOptions"
            value-key="EquipmentId"
            :label-fn="equipmentLabel"
            :fixed-params="{ ClassId: 192 }"
            multiple
            collapse-tags
            placeholder="请选择设备"
            style="width: 250px"
          />
        </el-form-item>
        <el-form-item label="时间范围">
          <el-select v-model="timeRange" placeholder="请选择" @change="onTimeRangeChange" style="width: 140px">
            <el-option label="近一日" :value="0" />
            <el-option label="近七日" :value="1" />
            <el-option label="近一个月" :value="2" />
            <el-option label="近三个月" :value="3" />
            <el-option label="近半年" :value="4" />
            <el-option label="近一年" :value="5" />
          </el-select>
        </el-form-item>
        <el-form-item label="日期范围">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            @change="onDateRangeChange"
            style="width: 280px"
          />
        </el-form-item>
        <el-form-item label="班次">
          <el-select v-model="queryParams.ScheduleId" clearable placeholder="全部" style="width: 120px">
            <el-option
              v-for="item in scheduleOptions"
              :key="item.ScheduleId"
              :label="item.Schedule"
              :value="item.ScheduleId"
            />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleQuery">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
          <el-button type="success" icon="el-icon-download" @click="handleExport">导出</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 数据表格 -->
    <div class="table-wrapper">
      <el-table
        v-loading="loading"
        :data="tableData"
        border
        stripe
        style="width: 100%"
        height="100%"
        show-summary
        :summary-method="getSummaries"
      >
      <el-table-column prop="DpName" label="车间" min-width="100" align="center" />
      <el-table-column prop="EquipmentNo" label="设备编号" min-width="100" align="center" />
      <el-table-column prop="EquipmentName" label="设备名称" min-width="120" align="center" />
      <el-table-column prop="WorkDate" label="日期" min-width="120" align="center">
        <template slot-scope="{ row }">
          {{ formatDate(row.WorkDate) }}
        </template>
      </el-table-column>
      <el-table-column prop="ScheduleName" label="班次" min-width="100" align="center" />
      <el-table-column prop="PlanWorkHours" label="计划工时(h)" width="110" align="center" />
      <el-table-column prop="ProductionHours" label="生产工时(h)" width="110" align="center" />
      <el-table-column prop="StandbyHours" label="待机工时(h)" width="110" align="center" />
      <el-table-column prop="FailureHours" label="故障工时(h)" width="110" align="center" />
      <el-table-column prop="UtilizationRate" label="生产利用率(%)" width="120" align="center">
        <template slot-scope="{ row }">
          {{ formatRate(row.UtilizationRate) }}
        </template>
      </el-table-column>
      <el-table-column prop="MoldCount" label="生产模数" min-width="100" align="center" />
      </el-table>

      <!-- 分页 -->
      <el-pagination
      v-show="total > 0"
      :current-page="queryParams.PageIndex"
      :page-size="queryParams.PageSize"
      :page-sizes="[20, 50, 100, 200]"
      :total="total"
      layout="total, sizes, prev, pager, next, jumper"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
        style="margin-top: 5px; text-align: right"
      />
    </div>

    <!-- 汇总信息 -->
    <el-card v-if="summary" class="summary-card" shadow="never" style="margin-top: 5px">
      <div slot="header">
        <span>统计汇总</span>
        <span style="margin-left: 20px; color: #909399">共 {{ summary.TotalCount }} 条记录</span>
      </div>
      <el-row :gutter="20">
        <el-col :span="4">
          <div class="summary-item">
            <div class="summary-label">计划工时</div>
            <div class="summary-value">{{ formatNumber(summary.TotalPlanWorkHours) }} h</div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="summary-item">
            <div class="summary-label">生产工时</div>
            <div class="summary-value">{{ formatNumber(summary.TotalProductionHours) }} h</div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="summary-item">
            <div class="summary-label">待机工时</div>
            <div class="summary-value">{{ formatNumber(summary.TotalStandbyHours) }} h</div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="summary-item">
            <div class="summary-label">故障工时</div>
            <div class="summary-value">{{ formatNumber(summary.TotalFailureHours) }} h</div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="summary-item">
            <div class="summary-label">平均利用率</div>
            <div class="summary-value">{{ formatRate(summary.AvgUtilizationRate) }}%</div>
          </div>
        </el-col>
        <el-col :span="4">
          <div class="summary-item">
            <div class="summary-label">生产模数</div>
            <div class="summary-value">{{ summary.TotalMoldCount }}</div>
          </div>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
import shiftWorkhoursApi from '@/api/equipment/shiftWorkhours'
import equipmentApi from '@/api/ba/equipment'
import dpApi from '@/api/ent/dp'
import scheduleApi from '@/api/ba/schedule'
import pageSelect from '@/components/common/pageSelect'

export default {
  name: 'ShiftWorkhours',
  components: { pageSelect },
  data() {
    return {
      // 加载状态
      loading: false,
      // 表格数据
      tableData: [],
      // 总条数
      total: 0,
      // 汇总数据
      summary: null,
      // 查询参数
      queryParams: {
        DpIds: [],
        EquipmentIds: [],
        StartDate: null,
        EndDate: null,
        ScheduleId: null,
        PageIndex: 1,
        PageSize: 20
      },
      // 时间范围快捷选择（0-5对应不同时间范围）
      timeRange: 0,  // 默认近一日
      // 日期范围
      dateRange: [],
      // 下拉选项
      dpOptions: [],
      scheduleOptions: []
    }
  },
  mounted() {
    this.initOptions()
    // 默认选择近一日
    this.onTimeRangeChange(0)
  },
  methods: {
    // 初始化下拉选项
    async initOptions() {
      try {
        // 获取部门列表（车间）
        const dpRes = await dpApi.list({ PageIndex: 1, PageSize: 1000 })
        if (dpRes.Code === 0) {
          this.dpOptions = dpRes.Data || []
        }

        // 获取班次列表
        const scheduleRes = await scheduleApi.list({ PageIndex: 1, PageSize: 100 })
        if (scheduleRes.Code === 0) {
          this.scheduleOptions = scheduleRes.Data || []
        }
      } catch (error) {
        console.error('初始化选项失败:', error)
      }
    },

    // 设备下拉改远程搜索分页加载（pageSelect 组件），只出注塑设备（ClassId=192 由组件 fixedParams 下推后端过滤）
    loadEquipmentOptions(params) {
      return equipmentApi.list(params)
    },
    equipmentLabel(item) {
      return item.EquipmentNo + ' - ' + item.EquipmentName
    },

    // 时间范围快捷选择变化
    onTimeRangeChange(e) {
      let today = this.$commonJs.getToday()
      let now = (new Date(today)).getTime()
      let s = ""
      let result = ""
      switch (e) {
        case 0:
          s = new Date(now - 86400000)
          result = this.$commonJs.toDateString(s)
          today = result
          break
        case 1:
          s = new Date(now - 7 * 86400000)
          result = this.$commonJs.toDateString(s)
          break
        case 2:
          s = new Date(now - 30 * 86400000)
          result = this.$commonJs.toDateString(s)
          break
        case 3:
          s = new Date(now - 90 * 86400000)
          result = this.$commonJs.toDateString(s)
          break
        case 4:
          s = new Date(now - 180 * 86400000)
          result = this.$commonJs.toDateString(s)
          break
        case 5:
          s = new Date(now - 365 * 86400000)
          result = this.$commonJs.toDateString(s)
          break
      }
      this.dateRange = [result, today]
      this.handleQuery()
    },

    // 日期范围变化（手动选择时清除快捷选择）
    onDateRangeChange() {
      this.timeRange = null
      if (this.dateRange && this.dateRange.length === 2) {
        this.handleQuery()
      }
    },

    // 查询（重置到第1页）
    async handleQuery() {
      this.queryParams.PageIndex = 1
      this.fetchData()
    },

    // 实际请求数据
    async fetchData() {
      this.loading = true
      try {
        // 处理日期参数
        if (this.dateRange && this.dateRange.length === 2) {
          this.queryParams.StartDate = this.dateRange[0]
          this.queryParams.EndDate = this.dateRange[1]
        }

        const res = await shiftWorkhoursApi.list(this.queryParams)
        if (res.Code === 0) {
          this.tableData = res.Data?.Data || []
          this.total = res.Data?.Count || 0
          this.summary = res.Data?.Summary || null
        } else {
          this.$message.error(res.Msg || '查询失败')
        }
      } catch (error) {
        console.error('查询失败:', error)
        this.$message.error('查询失败')
      } finally {
        this.loading = false
      }
    },

    // 重置
    handleReset() {
      this.queryParams = {
        DpIds: [],
        EquipmentIds: [],
        StartDate: null,
        EndDate: null,
        ScheduleId: null,
        PageIndex: 1,
        PageSize: 20
      }
      this.timeRange = 1
      this.onTimeRangeChange(1)
    },

    // 导出
    async handleExport() {
      this.loading = true
      try {
        // 处理日期参数
        if (this.dateRange && this.dateRange.length === 2) {
          this.queryParams.StartDate = this.dateRange[0]
          this.queryParams.EndDate = this.dateRange[1]
        }
        const res = await shiftWorkhoursApi.export(this.queryParams)
        // res 是 axios response 对象，res.data 才是 blob 数据
        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `设备生产工时查询_${new Date().toISOString().slice(0, 10)}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      } finally {
        this.loading = false
      }
    },

    // 分页大小变化
    handleSizeChange(size) {
      this.queryParams.PageSize = size
      this.queryParams.PageIndex = 1 // 改变每页条数时重置到第1页
      this.handleQuery()
    },

    // 页码变化（不重置页码）
    handleCurrentChange(page) {
      this.queryParams.PageIndex = page
      this.fetchData()
    },

    // 合计行
    getSummaries(param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = '合计'
          return
        }
        const prop = column.property
        if (['PlanWorkHours', 'ProductionHours', 'StandbyHours', 'FailureHours', 'MoldCount'].includes(prop)) {
          const values = data.map(item => Number(item[prop]) || 0)
          sums[index] = values.reduce((prev, curr) => prev + curr, 0).toFixed(2)
          if (prop === 'MoldCount') {
            sums[index] = parseInt(sums[index])
          }
        } else if (prop === 'UtilizationRate') {
          // 利用率取平均
          const values = data.map(item => Number(item[prop]) || 0)
          if (values.length > 0) {
            sums[index] = (values.reduce((prev, curr) => prev + curr, 0) / values.length).toFixed(2) + '%'
          } else {
            sums[index] = '0%'
          }
        } else {
          sums[index] = ''
        }
      })
      return sums
    },

    // 格式化日期
    formatDate(date) {
      if (!date) return ''
      return date.substring(0, 10)
    },

    // 格式化日期字符串
    formatDateString(date) {
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    // 格式化比率
    formatRate(value) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toFixed(2)
    },

    // 格式化数字
    formatNumber(value) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toFixed(2)
    }
  }
}
</script>

<style lang="scss" scoped>
.shiftworkhours-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px); /* - header和导航栏 */
  overflow: hidden;
}

.filter-container {
  flex-shrink: 0;
  padding: 5px;
  background: #fff;
  margin-bottom: 5px;
  border-radius: 4px;
}

.table-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;

  .el-table {
    flex: 1;
    overflow: hidden;
  }

  .el-pagination {
    flex-shrink: 0;
    padding: 5px 0;
    background: #fff;
  }
}

.summary-card {
  flex-shrink: 0;
  margin-top: 5px;

  .summary-item {
    text-align: center;
    padding: 10px 0;

    .summary-label {
      font-size: 14px;
      color: #909399;
      margin-bottom: 8px;
    }

    .summary-value {
      font-size: 20px;
      font-weight: bold;
      color: #409eff;
    }
  }
}
</style>
